import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';

// ✅ Helper to get API base
const getApiBaseUrl = () => process.env.REACT_APP_API_URL || "http://localhost:8000";

const CATEGORY_OPTIONS = [
  { value: 'water', label: 'Water' },
  { value: 'electricity', label: 'Electricity' },
  { value: 'internet', label: 'Internet' },
  { value: 'administration', label: 'Administration' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'replacements', label: 'Replacements' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'other', label: 'Other' }
];

const COST_TYPE_OPTIONS = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'onetime', label: 'One time cost' },
  { value: 'variable', label: 'Variable' }
];

const OperationsCostsTab = ({ costs, apartmentId, onUpdate }) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentCost, setCurrentCost] = useState(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 5;
  const [formData, setFormData] = useState({
    apartment: '',
    name: '',
    category: 'other',
    cost_type: 'fixed',
    value: '',
    currency: '',
    start_date: '',
    end_date: '',
    date_incurred: '',
    invoice_number: '',
    is_active: true
  });

  const [currencies, setCurrencies] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [loadingApartments, setLoadingApartments] = useState(false);

  const baseUrl = getApiBaseUrl();

  // 🔹 Fetch currencies
  useEffect(() => {
    const fetchCurrencies = async () => {
      setLoadingCurrencies(true);
      try {
        const response = await fetch(`${baseUrl}/api/currencies/`);
        if (response.ok) {
          const data = await response.json();
          setCurrencies(data);
        } else {
          console.error('Failed to load currencies:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching currencies:', error);
      } finally {
        setLoadingCurrencies(false);
      }
    };
    fetchCurrencies();
  }, [baseUrl]);

  // 🔹 Fetch apartments
  useEffect(() => {
    const fetchApartments = async () => {
      setLoadingApartments(true);
      try {
        const response = await fetch(`${baseUrl}/api/properties/apartments/`);
        if (response.ok) {
          const data = await response.json();
          setApartments(data);
        } else {
          console.error('Failed to load apartments:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching apartments:', error);
      } finally {
        setLoadingApartments(false);
      }
    };
    fetchApartments();
  }, [baseUrl]);

  const handleEditClick = (cost) => {
    setCurrentCost(cost);
    setFormData({
      apartment: cost.apartment?.id || apartmentId || '',
      name: cost.name || '',
      category: cost.category || 'other',
      cost_type: cost.cost_type || 'fixed',
      value: cost.amount || cost.value || '',
      currency: cost.currency?.id || '',
      start_date: cost.start_date || '',
      end_date: cost.end_date || '',
      date_incurred: cost.date_incurred || '',
      invoice_number: cost.invoice_number || '',
      is_active: cost.is_active ?? true
    });
    setEditDialogOpen(true);
  };

  const handleAddClick = () => {
    const today = new Date().toISOString().split('T')[0];
    setCurrentCost(null);
    setFormData({
      apartment: apartmentId || '',
      name: '',
      category: 'other',
      cost_type: 'fixed',
      value: '',
      currency: '',
      start_date: '',
      end_date: '',
      date_incurred: today,
      invoice_number: '',
      is_active: true
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const url = currentCost
        ? `${baseUrl}/api/costs/operational-costs/${currentCost.id}/`
        : `${baseUrl}/api/costs/operational-costs/`;
      const method = currentCost ? 'PUT' : 'POST';

      const isOneTime = formData.cost_type === 'onetime';

      const payload = {
        name: formData.name,
        category: formData.category,
        cost_type: formData.cost_type,
        value: formData.value,
        apartment_id: formData.apartment || apartmentId || null,
        currency_id: formData.currency || null,
        start_date: isOneTime ? null : (formData.start_date || null),
        end_date: isOneTime ? null : (formData.end_date || null),
        date_incurred: formData.date_incurred || null,
        invoice_number: formData.invoice_number || null,
        is_active: !!formData.is_active
      };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setEditDialogOpen(false);
        onUpdate();
      } else {
        const errorData = await response.json();
        console.error('Error saving cost:', errorData);
      }
    } catch (error) {
      console.error('Error saving cost:', error);
    }
  };

  const handleDelete = async (costId) => {
    if (window.confirm('Are you sure you want to delete this cost?')) {
      try {
        const response = await fetch(`${baseUrl}/api/costs/operational-costs/${costId}/`, {
          method: 'DELETE'
        });
        if (response.ok) onUpdate();
      } catch (error) {
        console.error('Error deleting cost:', error);
      }
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Operational Costs</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleAddClick}>
          Add Cost
        </Button>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Apartment</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Start - End Dates</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {costs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((cost) => (
              <TableRow key={cost.id}>
                <TableCell>{cost.name}</TableCell>
                <TableCell>{cost.apartment?.apartment_name || '—'}</TableCell>
                <TableCell>
                  {CATEGORY_OPTIONS.find((o) => o.value === cost.category)?.label || cost.category}
                </TableCell>
                <TableCell>{cost.cost_type}</TableCell>
                <TableCell>
                  {cost.amount || cost.value} {cost.currency?.symbol || ''}
                </TableCell>
                <TableCell>
                  {cost.start_date && cost.end_date
                    ? `${new Date(cost.start_date).toLocaleDateString()} - ${new Date(
                        cost.end_date
                      ).toLocaleDateString()}`
                    : 'Always valid'}
                </TableCell>
                <TableCell>{cost.is_active ? 'Active' : 'Inactive'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleEditClick(cost)}>
                    <Edit />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(cost.id)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={costs.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[5]}
      />

      {/* =================== Add/Edit Dialog =================== */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{currentCost ? 'Edit Cost' : 'Add New Cost'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            
            {/* Apartment Selector */}
            <FormControl fullWidth>
              <InputLabel>Apartment</InputLabel>
              <Select
                value={formData.apartment || ''}
                label="Apartment"
                onChange={(e) => setFormData({ ...formData, apartment: e.target.value })}
              >
                {loadingApartments ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} /> &nbsp; Loading...
                  </MenuItem>
                ) : (
                  apartments.map((apt) => (
                    <MenuItem key={apt.id} value={apt.id}>
                      {apt.apartment_name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <TextField
              label="Cost Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Cost Type</InputLabel>
              <Select
                value={formData.cost_type}
                label="Cost Type"
                onChange={(e) => setFormData({ ...formData, cost_type: e.target.value })}
              >
                {COST_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Value"
              type="number"
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              fullWidth
              inputProps={{
                step: '0.01',
                min: formData.cost_type === 'percentage' ? 0 : undefined,
                max: formData.cost_type === 'percentage' ? 100 : undefined
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currency}
                label="Currency"
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              >
                {loadingCurrencies ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} /> &nbsp; Loading...
                  </MenuItem>
                ) : (
                  currencies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name} ({c.symbol})
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {formData.cost_type !== 'onetime' && (
                <>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={formData.start_date || ''}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    value={formData.end_date || ''}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </>
              )}
              <TextField
                label="Date Incurred"
                type="date"
                value={formData.date_incurred || ''}
                onChange={(e) => setFormData({ ...formData, date_incurred: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>

            <TextField
              label="Invoice / Ticket Number"
              value={formData.invoice_number}
              onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.is_active}
                label="Status"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    is_active: e.target.value === 'true' || e.target.value === true
                  })
                }
              >
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperationsCostsTab;
